/** Renders AI replies as Markdown within narrow inspector panels. */
import { memo, type ReactNode } from 'react';
import Markdown, { defaultUrlTransform, type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ClipboardList, Database, FileText, Layers, ListChecks, Ruler, TriangleAlert } from './icons';

interface MarkdownNode { type: string; value?: string; alt?: string; children?: MarkdownNode[] }
const emojiDecoration = /[0-9#*]\uFE0F?\u20E3|\p{Regional_Indicator}{2}|(?:\p{Extended_Pictographic}|\p{Emoji_Presentation})(?:\uFE0F|\uFE0E)?\p{Emoji_Modifier}?(?:\u200D(?:\p{Extended_Pictographic}|\p{Emoji_Presentation})(?:\uFE0F|\uFE0E)?\p{Emoji_Modifier}?)*|\p{Emoji_Modifier}/gu;
const cleanDecorationText = (text: string) => text.replace(emojiDecoration, value => {
  if (/^[0-9#*]\uFE0F?\u20E3$/.test(value)) return value[0];
  return /^[©®™↔↕↖↗↘↙↩↪]$/.test(value) ? value : '';
});

function cleanDecorations() {
  return (tree: MarkdownNode): void => {
    const visit = (node: MarkdownNode): void => {
      if (node.type === 'text' && node.value) node.value = cleanDecorationText(node.value);
      if (node.type === 'image' && node.alt) node.alt = cleanDecorationText(node.alt).trim();
      node.children?.forEach(visit);
      if ((node.type === 'heading' || node.type === 'paragraph') && node.children?.[0]?.type === 'text')
        node.children[0].value = node.children[0].value?.trimStart();
    };
    visit(tree);
  };
}

function headingText(node?: MarkdownNode): string {
  return node?.value ?? node?.children?.map(headingText).join('') ?? '';
}

function ResponseHeading({ level, children, node }: { level: 1 | 2 | 3 | 4 | 5 | 6; children?: ReactNode; node?: MarkdownNode }) {
  const text = headingText(node);
  const Icon = /\b(?:warning|limitations?|unresolved|blocked|needs review)\b/i.test(text) ? TriangleAlert
    : /\b(?:calibration|scale|measurements?|quantit(?:y|ies)|points?)\b/i.test(text) ? Ruler
    : /\b(?:saved|stored|database|backend)\b/i.test(text) ? Database
    : /\b(?:drawings?|documents?|specifications?|sources?|project)\b/i.test(text) ? FileText
    : /\b(?:brickwork|masonry|wall|layers?|assembly|build[ -]?up)\b/i.test(text) ? Layers
    : /\b(?:next steps|tasks?|actions?)\b/i.test(text) ? ListChecks
    : /\b(?:summary|review)\b/i.test(text) ? ClipboardList : null;
  const Heading = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  return <Heading className={`flex items-start gap-2 font-semibold leading-snug ${level === 1 ? 'text-lg' : level === 2 ? 'text-base' : ''}`}>
    {Icon && <Icon size={16} className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden="true" />}
    <span className="min-w-0">{children}</span>
  </Heading>;
}

const components: Components = {
  p: ({ children }) => <p className="whitespace-pre-wrap">{children}</p>,
  h1: ({ children, node }) => <ResponseHeading level={1} node={node}>{children}</ResponseHeading>,
  h2: ({ children, node }) => <ResponseHeading level={2} node={node}>{children}</ResponseHeading>,
  h3: ({ children, node }) => <ResponseHeading level={3} node={node}>{children}</ResponseHeading>,
  h4: ({ children, node }) => <ResponseHeading level={4} node={node}>{children}</ResponseHeading>,
  h5: ({ children, node }) => <ResponseHeading level={5} node={node}>{children}</ResponseHeading>,
  h6: ({ children, node }) => <ResponseHeading level={6} node={node}>{children}</ResponseHeading>,
  ul: ({ children }) => <ul className="list-disc space-y-1 pl-5">{children}</ul>,
  ol: ({ children, start }) => <ol start={start} className="list-decimal space-y-1 pl-5">{children}</ol>,
  li: ({ children }) => <li className="min-w-0 [&>p]:my-1">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  blockquote: ({ children }) => <blockquote className="space-y-2 border-l-2 border-border pl-3 text-muted-foreground">{children}</blockquote>,
  pre: ({ children }) => <pre className="max-w-full overflow-x-auto rounded-lg bg-muted p-3 text-xs [overflow-wrap:normal] [&>code]:bg-transparent [&>code]:p-0">{children}</pre>,
  code: ({ children, className }) => <code className={`rounded bg-muted px-1 py-0.5 font-mono text-[0.9em] ${className ?? ''}`}>{children}</code>,
  table: ({ children }) => <div className="max-w-full overflow-x-auto"><table className="w-full border-collapse text-left text-xs">{children}</table></div>,
  th: ({ children, style }) => <th style={style} className="border border-border bg-muted/50 px-2 py-1.5 align-top font-semibold">{children}</th>,
  td: ({ children, style }) => <td style={style} className="border border-border px-2 py-1.5 align-top">{children}</td>,
  a: ({ children, href, title }) => href
    ? <a href={href} title={title} className="text-primary underline underline-offset-2" target={/^https?:|^\/\//i.test(href) ? '_blank' : undefined} rel="noopener noreferrer">{children}</a>
    : <span>{children}</span>,
  img: ({ alt }) => alt ? <span>{alt}</span> : null,
  hr: () => <hr className="border-border" />,
};

function safeUrl(url: string): string {
  const value = defaultUrlTransform(url);
  if (!value) return '';
  try {
    return ['http:', 'https:', 'mailto:'].includes(new URL(value, 'https://quantis.invalid').protocol) ? value : '';
  } catch {
    return '';
  }
}

export const AIResponseMarkdown = memo(function AIResponseMarkdown({ text }: { text: string }) {
  return <div className="min-w-0 max-w-full space-y-3 text-sm leading-relaxed [overflow-wrap:anywhere]">
    <Markdown remarkPlugins={[remarkGfm, cleanDecorations]} skipHtml urlTransform={safeUrl} components={components}>{text}</Markdown>
  </div>;
});
